import React, { useState } from 'react';
import UndertakingForm from './components/UndertakingForm';
import AdminPanel from './components/AdminPanel';
import AdminLogin from './components/AdminLogin';
import AssetDetails from "./components/AssetDetails"
import User from "./components/Users/User";
import EditForm from './components/EditUndertakingForm';
import SettingsPage from './components/Settings/SettingsPage';
import Assets from './components/Assets/Assets';
import Analytics from './components/Analytics/Analytics';
import Integrations from './components/Integrations/Integrations';
import Acknowledge from './components/Acknowledge/Acknowledge';
import SetPassword from './components/SetPassword/SetPassword';
import MyDevices from './components/Employee/MyDevices';
import DiscrepancyReview from './components/Reviews/DiscrepancyReview';
import AssetVerification from './components/Verification/AssetVerification';
import AuditLogPage from './components/Audit/AuditLogPage';
import WarrantyReport from './components/Reports/WarrantyReport';
import ComplianceReport from './components/Reports/ComplianceReport';
import Organizations from './components/Organizations/Organizations';
import SsoCallback from './components/SsoCallback';
import Landing from './components/Landing/Landing';
import {
  canAccessIntegrations,
  canAccessSettings,
  canManageOrganizations,
  canManageUsers,
} from './utils/roles';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import ThankYouPage from "../src/components/Thankyoupage"
import { logoutSession } from './utils/session';
import { postLoginPath } from './utils/postLoginPath';
import axios from './api';

function App() {
const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(() => {
  return localStorage.getItem('isAdminLoggedIn') === 'true';
});
  const LoginRoute = () => {
    const navigate = useNavigate();
    const handleLogin = (user, meta) => {
    setIsAdminLoggedIn(true);
    localStorage.setItem('isAdminLoggedIn', 'true');
    if (meta?.requirePasswordChange) {
      localStorage.setItem('requirePasswordChange', 'true');
    } else {
      localStorage.removeItem('requirePasswordChange');
    }

    const loggedInUser = user || JSON.parse(localStorage.getItem('adminUser'));
      navigate(postLoginPath(loggedInUser?.role));
      };
    return <AdminLogin onLogin={handleLogin} />;
  };

  // Public self-signup is disabled by default (ALLOW_PUBLIC_REGISTER on backend).
  const RegisterRoute = () => <Navigate to="/login" replace />;
  
  const UsersRoute = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('adminUser') || 'null');
    const token = localStorage.getItem('adminToken');
    if (!user || !token || user.role === 'Employee') return <Navigate to="/login" />;
    if (!canManageUsers(user.role)) return <Navigate to="/assets" />;
    return <User onLogout={() => doLogout(navigate)} />;
  };

  const doLogout = async (navigate) => {
    setIsAdminLoggedIn(false);
    await logoutSession(axios);
    navigate('/login');
  };

  const AdminRoute = () => {
    const navigate = useNavigate();
    const handleLogout = () => doLogout(navigate);
    const user = JSON.parse(localStorage.getItem('adminUser'));
    const token = localStorage.getItem('adminToken');
    return user && token ? (
      <AdminPanel onLogout={handleLogout} />
        ) : (
       <Navigate to="/login" />
     )
  };
  const AssetRoute = () => {
    const navigate = useNavigate();
    const handleLogout = () => doLogout(navigate);
    return isAdminLoggedIn && localStorage.getItem('adminToken') ? (
      <AssetDetails onLogout={handleLogout} />
    ) : (
      <Navigate to="/login" />
    );
  };

  const AdminGuard = ({ children }) => {
    const navigate = useNavigate();
    const handleLogout = () => doLogout(navigate);
    const user = JSON.parse(localStorage.getItem('adminUser'));
    const token = localStorage.getItem('adminToken');
    return user && token && user.role !== 'Employee'
      ? children(handleLogout)
      : <Navigate to="/login" />;
  };

  const IntegrationsRoute = () => (
    <AdminGuard>{(logout) => {
      const user = JSON.parse(localStorage.getItem('adminUser') || 'null');
      if (!canAccessIntegrations(user?.role)) return <Navigate to="/assets" />;
      return <Integrations onLogout={logout} />;
    }}</AdminGuard>
  );
  const SettingsRoute = () => (
    <AdminGuard>{(logout) => {
      const user = JSON.parse(localStorage.getItem('adminUser') || 'null');
      if (!canAccessSettings(user?.role)) return <Navigate to="/assets" />;
      return <SettingsPage onLogout={logout} />;
    }}</AdminGuard>
  );
  const AssetsRoute = () => (
    <AdminGuard>{(logout) => <Assets onLogout={logout} />}</AdminGuard>
  );
  const AnalyticsRoute = () => (
    <AdminGuard>{(logout) => <Analytics onLogout={logout} />}</AdminGuard>
  );
  const ReviewsRoute = () => (
    <AdminGuard>{(logout) => <DiscrepancyReview onLogout={logout} />}</AdminGuard>
  );
  const VerificationRoute = () => (
    <AdminGuard>{(logout) => <AssetVerification onLogout={logout} />}</AdminGuard>
  );
  const AuditRoute = () => (
    <AdminGuard>{(logout) => <AuditLogPage onLogout={logout} />}</AdminGuard>
  );
  const WarrantyRoute = () => (
    <AdminGuard>{(logout) => <WarrantyReport onLogout={logout} />}</AdminGuard>
  );
  const ComplianceRoute = () => (
    <AdminGuard>{(logout) => <ComplianceReport onLogout={logout} />}</AdminGuard>
  );
  const OrganizationsRoute = () => (
    <AdminGuard>{(logout) => {
      const user = JSON.parse(localStorage.getItem('adminUser') || 'null');
      if (!canManageOrganizations(user?.role)) return <Navigate to="/assets" />;
      return <Organizations onLogout={logout} />;
    }}</AdminGuard>
  );


  return (
    <Router>
      <Routes>
        <Route path="/register" element={<RegisterRoute />} />
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/login/sso-callback" element={<SsoCallback />} />
        <Route path="/dashboard" element={<AdminRoute />} />
        <Route path="/users" element={<UsersRoute />} />
        <Route path="/form" element={<UndertakingForm />} />
        <Route path="/acknowledge" element={<Acknowledge />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/my-devices" element={<MyDevices />} />
        <Route path="/edit-form/:id" element={<EditForm />} />
        <Route path="/asset" element={<AssetRoute />} />
        <Route path="/settings" element={<SettingsRoute />} />
        <Route path="/assets" element={<AssetsRoute />} />
        <Route path="/analytics" element={<AnalyticsRoute />} />
        <Route path="/integrations" element={<IntegrationsRoute />} />
        <Route path="/reviews" element={<ReviewsRoute />} />
        <Route path="/verification" element={<VerificationRoute />} />
        <Route path="/audit" element={<AuditRoute />} />
        <Route path="/warranty" element={<WarrantyRoute />} />
        <Route path="/compliance" element={<ComplianceRoute />} />
        <Route path="/organizations" element={<OrganizationsRoute />} />
        <Route path="/" element={<Landing />} />
        <Route path="/thank-you" element={<ThankYouPage/>} />
      </Routes>
    </Router>
  );
}

export default App;
